import { useEffect, useState } from "react";
import { useNavigate, useResolvedPath, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  LocalShippingOutlined,
  SendOutlined,
  West,
} from "@mui/icons-material";
import moment from "moment";
import "moment-timezone";
import { OrderStatus } from "@/enums";
import { baseUrl } from "@/consts";
import { OrderFull, CartProduct, Cart, Order, Settings } from "@/interfaces";
import { useDataApi } from "@/hooks";
import {
  formatAddress,
  formatAxiosError,
  formatCurrency,
  formatName,
  isNonEmptyArray,
  isNonEmptyNumber,
  isNonEmptyObject,
  isNonEmptyString,
  isStringEqualCI,
} from "@/utils";
import { CartHelper, MuiHelper, OrderHelper } from "@/helpers";
import {
  colors,
  sxButtonBase,
  sxFont_12_400,
  sxFont_14_400,
  sxFont_16_400,
  sxFont_16_500,
  sxFont_20_400,
  sxFont_20_500,
  sxFont_22_400,
  sxFont_22_500,
  sxFont_24_400,
  sxFont_24_500,
  sxFont_34_500,
} from "@/theme";
import { ErrorMessage } from "@/components/atoms";
import { useSettings } from "@/contexts";
import axios from "axios";
import { OrderTrackApiResponse, TrackingRecord } from "@/components/pages/ShippingPortal/interface";
import FedExTrackingDialog from "./FedExTrackingDialog";

const sxShippingDataText = { ...sxFont_16_400 };

const sxOrderGroupHeaderTitle = {
  ...sxFont_24_500,
  color: colors.neutralGrey800,
};

const sxOrderProductSummaryText = { ...sxFont_16_400 };

const imgWidth = "100px";

const getOrderProductImage = (product?: CartProduct) =>
  MuiHelper.getCenteredImage(
    product?.productImageUrl,
    product?.productDescription,
    product?.productDescription,
    imgWidth
  );



const getOrderProductInfo = (product?: CartProduct, shipped?: boolean, settings?: Settings) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        rowGap: "8px",
        color: colors.neutralGrey800,
      }}
    >
      <Typography component="div" sx={sxFont_22_400}>
        {product?.productDescription}
      </Typography>
      <Typography component="div" sx={sxFont_12_400}>
        {`SKU# ${product?.sku}`}
      </Typography>
      {/* <Typography component="div" sx={sxFont_16_400}>
        {`${formatCurrency(product?.price as number)} Retail${isNonEmptyNumber(product?.coopApplied)
          ? ` ${formatCurrency((product?.price || 0) - ((product?.price || 0) * (settings?.coopEligible || 0)))} w/ Co-Op`
          : ""
          }`}
      </Typography> */}
      <Typography component="div" sx={sxFont_14_400}>
        {`Quantity: ${shipped
          ? product?.shippingQuantity || 0
          : (product?.quantity || 0)
          }`}
      </Typography>
    </Box>
  )
};

const getCoopDiscount = (product?: CartProduct, shipped?: boolean)=>{
 return (((product?.coopApplied??0)/(CartHelper.getItemShippedQuantity(product)+CartHelper.getItemInStoreQuantity(product))) *
          (shipped
            ? CartHelper.getItemShippedQuantity(product)
            : CartHelper.getItemInStoreQuantity(product))) || 0
        
}

const getProductSubTotal = (product?: CartProduct, shipped?: boolean)=>{
  return  shipped
  ? CartHelper.getItemShippedSubtotal(product)
  : CartHelper.getItemInStoreSubtotal(product)
}

const getOrderProductSummary = (product?: CartProduct, shipped?: boolean) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr",
      justifyItems: "end",
      alignItems: "center",
      color: colors.neutralGrey800,
    }}
  >
    <Typography component="div" sx={sxOrderProductSummaryText}>
      {`${formatCurrency(getProductSubTotal(product,shipped))} Retail`}
    </Typography>
    <Typography component="div" sx={sxOrderProductSummaryText}>
      {isNonEmptyNumber(product?.coopApplied)
        ? `-${formatCurrency(getCoopDiscount(product,shipped))} Co-Op`
        : undefined}
    </Typography>

    <Typography
      component="div"
      sx={{ ...sxFont_24_500, color: colors.primaryOzone500 }}
    >
      {formatCurrency(getProductSubTotal(product,shipped) - getCoopDiscount(product,shipped))}
    </Typography>
  </Box>
);

const getOrderProductItem = (product?: CartProduct, shipped?: boolean, settings?: Settings) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 2fr 1fr",
      "&:not(:first-of-type)": { paddingTop: "16px" },
      "&:not(:last-of-type)": {
        paddingBottom: "16px",
        borderBottom: `1px solid ${colors.neutralGrey400}`,
      },
    }}
  >
    {getOrderProductImage(product)}
    {getOrderProductInfo(product, shipped, settings)}
    {getOrderProductSummary(product, shipped)}
  </Box>
);

const getOrderInStoreItems = (items?: CartProduct[],settings?:Settings) => (
  <Box sx={{ padding: "16px" }}>
    <Typography
      component="div"
      sx={{ ...sxFont_24_500, color: colors.neutralGrey800 }}
    >
      In-Store Items
      {isNonEmptyArray(items)
        ? ` (${CartHelper.getInStoreItemsQuantity(items)})`
        : undefined}
    </Typography>
    {isNonEmptyArray(items) ? <Divider sx={{ margin: "16px 0" }} /> : undefined}
    <Box>
      {isNonEmptyArray(items)
        ? items?.map((item) => getOrderProductItem(item,false, settings))
        : undefined}
    </Box>
  </Box>
);

const handleViewTrackingInfoClick = async (orderId?: number, setTrackLoading?: React.Dispatch<React.SetStateAction<boolean>>, setTrackNumbers?: React.Dispatch<React.SetStateAction<string[]>>) => {
  try {
    setTrackLoading?.(true)
    const response = await axios.get<OrderTrackApiResponse[]>(`${baseUrl.ORDER}/getorderboxes/${orderId}`)
    const trackingNumbers = response.data.map(item => item.trackingNumber)
    setTrackNumbers?.(trackingNumbers)
    // window.open(`${baseUrl.FedEx}/trackpackage?trackingNumber=${trackingNumbers ?? ""}`,'_blank')
    // console.log({ trackingNumbers })
    // if (!trackingNumbers.length) return alert("No Tracking numbers found?");

    // // all tracking ids call:
    // const allTrackingPromises = trackingNumbers.map(item => axios.get<TrackingRecord>(`${baseUrl.FedEx}/trackpackage?trackingNumber=${item}`))
    // const allTrackingsResponse = await Promise.all(allTrackingPromises)
    // const trackingRecords = allTrackingsResponse.filter(item => item.status == 200).flatMap(item => item.data.trackingItems)
    // return trackingRecords
    // alert("Could you please clarify what should be done?");
  } catch (error) {
    console.log(error)
    return error
  }
  finally {
    setTrackLoading?.(false)

  }
};

const getOrderShipItems = (items?: CartProduct[], settings?: Settings, order?: OrderFull, trackLoading?: boolean, setTrackLoading?: React.Dispatch<React.SetStateAction<boolean>>, setErrorMessage?: React.Dispatch<React.SetStateAction<string>>, setTrackNumbers?: React.Dispatch<React.SetStateAction<string[]>>) => {

  const handleClick = async () => {
    try {
      const response = await handleViewTrackingInfoClick(order?.orderId, setTrackLoading, setTrackNumbers)
      setErrorMessage?.(response as any)
    } catch (error) {
      setErrorMessage?.(error as any)
    }
  }
  return <Box sx={{ padding: "16px" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        alignItems: "center",
      }}
    >
      <Typography
        component="div"
        sx={{ ...sxFont_24_500, color: colors.neutralGrey800 }}
      >
        Ship Items
        {isNonEmptyArray(items)
          ? ` (${CartHelper.getShippedItemsQuantity(items)})`
          : undefined}
      </Typography>
      <Button
        onClick={handleClick}
        startIcon={trackLoading ? <CircularProgress size={22} /> : <LocalShippingOutlined />}
        sx={{
          justifySelf: "end",
          ...sxFont_22_500,
          color: colors.neutralGrey800,
          backgroundColor: colors.solar,
          ...sxButtonBase,
        }}
      >
        View Tracking Info
      </Button>
    </Box>
    {isNonEmptyArray(items) ? <Divider sx={{ margin: "16px 0" }} /> : undefined}
    <Box>
      {isNonEmptyArray(items)
        ? items?.map((item) => getOrderProductItem(item, true, settings))
        : undefined}
    </Box>
  </Box>
}


const getOrderSummaryDataItem = (label?: string, value?: string) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      alignItems: "center",
      "&:not(:last-of-type)": {
        paddingBottom: "16px",
      },
    }}
  >
    <Typography
      component="div"
      sx={{
        ...sxFont_20_400,
        alignSelf: "start",
        textAlign: "start",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        ...sxFont_22_400,
        alignSelf: "end",
        textAlign: "end",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const getOrderSummaryDataItems = (order?: OrderFull) => (
  <Box sx={{ color: colors.neutralGrey800 }}>
    {getOrderSummaryDataItem(
      "Subtotal",
      formatCurrency(order?.total as number)
    )}
    {isNonEmptyNumber(order?.coopUsed)
      ? getOrderSummaryDataItem(
        "Eligible Co-Op applied",
        `-${formatCurrency(order?.coopUsed as number)}`
      )
      : undefined}
    {isNonEmptyNumber(order?.discount)
      ? getOrderSummaryDataItem(
        "Discounts",
        `-${formatCurrency(order?.discount as number)}`
      )
      : undefined}
    {/* Gift Card intentionally NOT shown here. It's a payment method, not a
        price reduction, so it now lives in the breakdown beneath the Order
        Total (see getOrderGiftCardBreakdown). Subtracting it up here is what
        drove the displayed total to $0 on gift-card orders. */}

    {isNonEmptyNumber(order?.shippingFee)
      ? getOrderSummaryDataItem(
        "Shipping & handling",
        formatCurrency(order?.shippingFee as number)
      )
      : undefined}


  </Box>
);

const getOrderSummaryDataHeader = (order?: OrderFull) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      alignItems: "center",
      alignContent: "center",
    }}
  >
    <Typography component="div" sx={sxOrderGroupHeaderTitle}>
      {`${order?.offsite ? "Offsite Order" : "In-StoreOrder"} #${order?.orderId}`}
    </Typography>
    <Typography
      component="div"
      sx={{
        ...sxFont_16_400,
        color: colors.primaryOzone900,
        textAlign: "end",
      }}
    >
      {moment(order?.dateTimeCreated)
        .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
        .format("MM/DD/YYYY hh:mm a z")
      }
    </Typography>
  </Box>
);

const getOrderSummaryDataFooter = (order?: OrderFull) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      alignItems: "center",
      alignContent: "center",
      paddingTop: "24px",
    }}
  >
    <Typography
      component="div"
      sx={{ ...sxFont_24_500, color: colors.neutralGrey800 }}
    >{`Order Total (${CartHelper.getTotalQuantity(
      order as unknown as Cart
    )} items)`}</Typography>
    <Typography
      component="div"
      sx={{
        ...sxFont_34_500,
        color: colors.primaryOzone500,
        alignSelf: "end",
        textAlign: "end",
      }}
    >
      {formatCurrency(OrderHelper.getOrderTotal(order))}
    </Typography>
  </Box>
);

// Gift-card breakdown shown directly beneath the Order Total. Only rendered
// when a gift card was actually used. Makes the gift card read as a payment
// against the (now correct) order total rather than something that zeroed the
// total out. Shows the unspent balance when the card was worth more than the
// order, and the remaining Balance Due (what hit the credit card) otherwise.
const getOrderSummaryGiftCard = (order?: OrderFull) => {
  if (!isNonEmptyNumber(order?.couponUsed)) {
    return undefined;
  }

  const applied = OrderHelper.getGiftCardApplied(order);
  const leftover = OrderHelper.getGiftCardLeftover(order);
  const balanceDue = OrderHelper.getBalanceDue(order);

  return (
    <Box sx={{ paddingTop: "16px" }}>
      <Divider sx={{ marginBottom: "16px" }} />
      {getOrderSummaryDataItem("Gift Card", `-${formatCurrency(applied)}`)}
      {leftover > 0 ? (
        <Typography
          component="div"
          sx={{
            ...sxFont_14_400,
            color: colors.neutralGrey800,
            textAlign: "end",
            paddingBottom: "16px",
          }}
        >
          {`${formatCurrency(leftover)} gift card balance remaining`}
        </Typography>
      ) : undefined}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
        }}
      >
        <Typography
          component="div"
          sx={{ ...sxFont_24_500, color: colors.neutralGrey800 }}
        >
          Balance Due
        </Typography>
        <Typography
          component="div"
          sx={{
            ...sxFont_24_500,
            color: colors.primaryOzone500,
            alignSelf: "end",
            textAlign: "end",
          }}
        >
          {formatCurrency(balanceDue)}
        </Typography>
      </Box>
    </Box>
  );
};

const getOrderSummaryData = (order?: OrderFull) => (
  <Box
    sx={{
      borderRadius: "16px",
      padding: "16px",
      backgroundColor: colors.white,
    }}
  >
    {getOrderSummaryDataHeader(order)}
    <Divider sx={{ margin: "16px 0" }} />
    {getOrderSummaryDataItems(order)}
    {getOrderSummaryDataFooter(order)}
    {getOrderSummaryGiftCard(order)}
  </Box>
);

const getOrderOtherDataHeader = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr",
      alignItems: "start",
      alignContent: "start",
    }}
  >
    <Typography component="div" sx={sxOrderGroupHeaderTitle}>
      Customer Info
    </Typography>
  </Box>
);

const getDiscountCommentHeader = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr",
      alignItems: "start",
      alignContent: "start",
    }}
  >
    <Typography component="div" sx={sxOrderGroupHeaderTitle}>
      Discount Comment
    </Typography>
  </Box>
);

const getDiscountCommentData = (order?: OrderFull) => (
  <Box sx={{ color: colors.neutralGrey800 }}>
    <Typography component="div" sx={{ ...sxFont_16_400 }}>
      {order?.discountComment || "No discount comment"}
    </Typography>
  </Box>
);

const getOrderCustomerData = (order?: OrderFull) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      color: colors.neutralGrey800,
    }}
  >
    <Box>
      <Typography
        component="div"
        sx={{
          ...sxFont_16_400,
        }}
      >
        {formatName(order?.dealerFirstName, order?.dealerLastName)}
      </Typography>
      <Typography
        component="div"
        sx={{
          ...sxFont_16_500,
          textDecoration: "underline",
        }}
      >
        {order?.email}
      </Typography>
    </Box>
    <Typography
      component="div"
      sx={{ ...sxFont_16_400, alignSelf: "center", justifySelf: "end" }}
    >
      {order?.masterDealerId}
    </Typography>
  </Box>
);

const getOrderShippingData = (order?: OrderFull) => (
  <Box sx={{ marginTop: "48px", color: colors.neutralGrey800 }}>
    <Typography component="div" sx={{ ...sxFont_20_400 }}>
      Shipping address
    </Typography>
    <Typography component="div" sx={sxShippingDataText}>
      {isNonEmptyString(order?.shippingData?.shippingContact?.firstName) ||
        isNonEmptyString(order?.shippingData?.shippingContact?.lastName)
        ? formatName(
          order?.shippingData?.shippingContact?.firstName,
          order?.shippingData?.shippingContact?.lastName
        )
        : undefined}
    </Typography>
    <Typography component="div" sx={sxShippingDataText}>
      {isNonEmptyString(order?.shippingData?.shippingAddress?.street)
        ? order?.shippingData?.shippingAddress?.street
        : undefined}
    </Typography>
    <Typography component="div" sx={sxShippingDataText}>
      {isNonEmptyString(order?.shippingData?.shippingAddress?.city) ||
        isNonEmptyString(order?.shippingData?.shippingAddress?.state) ||
        isNonEmptyString(order?.shippingData?.shippingAddress?.zip)
        ? formatAddress(
          order?.shippingData?.shippingAddress?.city,
          order?.shippingData?.shippingAddress?.state,
          order?.shippingData?.shippingAddress?.zip
        )
        : undefined}
    </Typography>
    <Typography component="div" sx={sxShippingDataText}>
      {isNonEmptyString(order?.shippingData?.shippingContact?.phone)
        ? order?.shippingData?.shippingContact?.phone
        : undefined}
    </Typography>
  </Box>
);

const getOrderCustomerAndShippingData = (order?: OrderFull) => {
  return CartHelper.hasShippingData(order as unknown as Cart) ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        rowGap: "16px",
      }}
    >
      <Box
        sx={{
          borderRadius: "16px",
          padding: "16px",
          backgroundColor: colors.white,
        }}
      >
        {getOrderOtherDataHeader()}
        <Divider sx={{ margin: "16px 0" }} />
        {CartHelper.hasShippingContact(order as unknown as Cart)
          ? getOrderCustomerData(order)
          : undefined}
        {CartHelper.hasShippingAddress(order as unknown as Cart)
          ? getOrderShippingData(order)
          : undefined}
      </Box>

      {isNonEmptyNumber(order?.discount) && isNonEmptyString(order?.discountComment) && (
        <Box
          sx={{
            borderRadius: "16px",
            padding: "16px",
            backgroundColor: colors.white,
          }}
        >
          {getDiscountCommentHeader()}
          <Divider sx={{ margin: "16px 0" }} />
          {getDiscountCommentData(order)}
        </Box>
      )}
    </Box>
  ) : undefined;
};

const getDialogContent = (order?: Order) => {
  const message = `Are you sure you want to ${order?.status === OrderStatus.PAID ? "cancel" : "delete"
    } this order?${order?.status === OrderStatus.PAID
      ? " This will cancel the credit card payment."
      : ""
    }`;

  return (
    <Typography
      component="div"
      sx={{
        ...sxFont_24_400,
        textAlign: "center",
        padding: "24px 24px 0 24px",
        color: colors.neutralGrey800,
      }}
    >
      {message}
    </Typography>
  );
};

const getConfirmButtonText = (order?: Order) =>
  `Confirm ${order?.status === OrderStatus.PAID ? "Cancel" : "Delete"}`;

const getOrderFullEndPoint = "getOrderFull";
const cancelEndPoint = "cancel";

const OrderEditForm: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const { _get, _delete, isLoading, response, error } = useDataApi();
  const [order, setOrder] = useState<Order | undefined>(undefined);
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false)
  const [trackingNumbers, setTrackNumbers] = useState<string[]>([])
  const [openTrackingDialog, setOpenTrackingDialog] = useState(false)

  const { settings } = useSettings()

  const parentPath = useResolvedPath("../orders").pathname;

  const handleBackToOrdersClick = () => {
    navigate(parentPath);
  };

  useEffect(() => {
    setErrorMessage(undefined);
    _get(`${baseUrl.ORDER}/${getOrderFullEndPoint}/${orderId}`);
  }, []);

  useEffect(() => {
    if (
      response?.request?.responseURL?.indexOf(getOrderFullEndPoint) > 0 &&
      response?.status === 200
    ) {
      setOrder(response?.data);
      console.log('Full Order Details:', response?.data);
      return;
    }

    if (response?.config?.method === "delete" && response?.status === 204) {
      handleBackToOrdersClick();
      return;
    }

    if (error?.config?.url?.indexOf(getOrderFullEndPoint) > 0) {
      setErrorMessage(formatAxiosError(error));
      return;
    }

    if (error?.config?.method === "delete") {
      setErrorMessage(formatAxiosError(error));
    }
  }, [response, error]);

  const getOrderProducts = (order?: OrderFull) => {
    const inStoreItems = CartHelper.getInStoreItems(order as unknown as Cart);
    const shippedItems = CartHelper.getShippedItems(order as unknown as Cart);

    return (
      <Box
        sx={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: colors.white,
        }}
      >
        <FedExTrackingDialog
          open={openTrackingDialog}
          onClose={() => setOpenTrackingDialog(false)}
          trackingNumbers={trackingNumbers}
        />
        {isNonEmptyArray(inStoreItems)
          ? getOrderInStoreItems(inStoreItems,settings)
          : undefined}
        {isNonEmptyArray(inStoreItems) && isNonEmptyArray(shippedItems) ? (
          <Divider />
        ) : undefined}
        {isNonEmptyArray(shippedItems)
          ? getOrderShipItems(shippedItems, settings, order, trackLoading, setTrackLoading, setErrorMessage as any, setTrackNumbers)
          : undefined}
      </Box>
    );
  };

  const handleEmailReceiptClick = () => {
    alert("Could you please clarify what should be sent?");
  };

  const handleCancelDeleteOrderClick = () => {
    if (
      order?.status !== OrderStatus.SAVED &&
      order?.status !== OrderStatus.PAID
    ) {
      return;
    }

    setIsDialogOpen(true);
  };

  const handleDialogClose = (_: any, reason: string) => {
    if (isStringEqualCI(reason, "backdropClick")) {
      return;
    }

    handleCancelButtonClick();
  };

  const handleCancelButtonClick = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmButtonClick = async () => {
    if (
      !isNonEmptyNumber(order?.orderId) ||
      (order?.status !== OrderStatus.SAVED &&
        order?.status !== OrderStatus.PAID)
    ) {
      return;
    }

    setErrorMessage(undefined);
    // Always go through the cancel endpoint regardless of status. The bare
    // DELETE /Orders/{id} route does NOT restore Product.InventoryCount, so a
    // saved order deleted via that path silently bleeds inventory. Cancel
    // restores InventoryCount for both Saved and Paid orders.
    await _delete(`${baseUrl.ORDER}/${cancelEndPoint}/${order?.orderId}`);

    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (trackingNumbers.length) setOpenTrackingDialog(true)
  }, [trackingNumbers])

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
          height: "120px",
          padding: "0 28px 0 40px",
        }}
      >
        <Button
          startIcon={<West />}
          onClick={handleBackToOrdersClick}
          sx={{
            justifySelf: "start",
            ...sxFont_24_400,
            color: colors.neutralGrey800,
            ...sxButtonBase,
          }}
        >
          Back to Orders
        </Button>
        <Box sx={{ justifySelf: "end" }}>
          {order?.status === OrderStatus.PAID ? (
            <Button
              onClick={handleEmailReceiptClick}
              endIcon={<SendOutlined />}
              sx={{
                ...sxFont_16_400,
                color: colors.neutralGrey800,
                ...sxButtonBase,
              }}
            >
              Email Receipt
            </Button>
          ) : undefined}
          {order?.status === OrderStatus.SAVED ||
            order?.status === OrderStatus.PAID ? (
            <Button
              onClick={handleCancelDeleteOrderClick}
              endIcon={<DeleteOutline />}
              sx={{
                ...sxFont_16_400,
                color: colors.accent3Error,
                ...sxButtonBase,
              }}
            >
              {`${order?.status === OrderStatus.PAID ? "Cancel" : "Delete"
                } Order`}
            </Button>
          ) : undefined}
        </Box>
      </Box>
      {isNonEmptyString(errorMessage) ? (
        <ErrorMessage errorMessage={errorMessage} />
      ) : undefined}
      {isNonEmptyObject(order) ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              columnGap: "16px",
              padding: "16px",
              backgroundColor: colors.neutralGrey300,
            }}
          >
            {getOrderProducts(order)}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                rowGap: "16px",
              }}
            >
              {getOrderSummaryData(order)}
              {getOrderCustomerAndShippingData(order)}
            </Box>
          </Box>
          <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="xs">
            <DialogContent>{getDialogContent(order)}</DialogContent>
            <DialogActions
              sx={{
                "&.MuiDialogActions-root": {
                  "justify-content": "center",
                  mb: 3
                },
              }}
            >
              <Button
                onClick={handleCancelButtonClick}
                variant="outlined"
                sx={{
                  ...sxFont_20_400,
                  color: colors.neutralGrey800,
                  ...sxButtonBase,
                  borderRadius: "8px",
                  padding: "12px 0",
                  width: "173px",
                  border: `1px solid ${colors.neutralGrey800}`,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmButtonClick}
                sx={{
                  ...sxFont_20_500,
                  color: colors.white,
                  backgroundColor: colors.accent3Error,
                  ...sxButtonBase,
                  borderRadius: "8px",
                  padding: "14px 0",
                  width: "173px",
                  border: `1px solid ${colors.accent3Error}`,
                }}
              >
                {getConfirmButtonText(order)}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : isLoading ? (
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      ) : !isLoading && isNonEmptyString(errorMessage) ? (
        <ErrorMessage errorMessage={errorMessage} />
      ) : undefined}
    </Box>
  );
};

OrderEditForm.displayName = "OrderEditForm";

export default OrderEditForm;
